import { useRef } from 'react'

import { styled } from 'styled-components'

import { Button, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import type { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import SaveButton from '@/components/listings/search/savedSearch/SaveButton'
import type { ListingFilterList } from '@/components/listings/search/SearchFilters'
import CategoryFilter from '@/components/listings/search/SearchFilters/filters/CategoryFilter'
import { CategoryThresholdApplied } from '@/components/listings/search/SearchFilters/filters/CategoryFilter/CategoryThresholdControl'
import EndTimeRangeFilter from '@/components/listings/search/SearchFilters/filters/EndTimeRangeFilter'
import MyBidsFilter from '@/components/listings/search/SearchFilters/filters/MyBidsFilter'
import NewSellerFilter from '@/components/listings/search/SearchFilters/filters/NewSellerFilter'
import StoreFrontIdFilter from '@/components/listings/search/SearchFilters/filters/StoreFrontIdFilter'

import { FiltersList } from './FiltersList'

type AppliedFilterProps = {
  filters: ListingFilterList
  searchCtx: ReturnType<typeof useListingSearch>
  excludeSavedSearch?: boolean
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
`

const ScrollContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow: auto;
  padding-bottom: 1rem;
`

export const ActiveBar = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CounterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`

const Counter = styled.div`
  ${Typography.Subtitle1}
  color: ${designColors.neutral.black};
`

const ResetBtn = styled(Button)`
  font-size: 0.75rem;
`

export const AppliedFilters = ({
  filters,
  searchCtx,
  excludeSavedSearch,
}: AppliedFilterProps) => {
  const { state, updateFilters, getActiveFilterCount, resetFilters } = searchCtx
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const activeFilterCount = getActiveFilterCount()

  if (!activeFilterCount) return null

  // `categories` (top-level) only has a live `.Applied` when `CategoryFilter`
  // is actually registered in `filters` — when the subcategory-filter flag
  // swaps it out for `CategoryTreeFilter` (which only renders `category`,
  // subcategory, chips), a `categories` value deep-linked in from elsewhere
  // (e.g. the Auction Schedule click-through) would otherwise render no chip
  // at all, even though it's still counted in `activeFilterCount` and still
  // narrowing results. Render it directly in that case, same as the other
  // no-sidebar-picker chips above.
  const categoryFilterMissingFromSidebar = !filters.includes(CategoryFilter)

  return (
    <Wrapper data-testid="applied-filters">
      <ActiveBar>
        <CounterWrapper>
          <Counter>
            <FormattedMessage
              id="Listings.filtersApplied"
              values={{
                count: activeFilterCount.toString(),
              }}
            />
          </Counter>

          {excludeSavedSearch ? null : <SaveButton />}

          <ResetBtn
            appearance="plainTertiary"
            size="small"
            onClick={resetFilters}
          >
            <FormattedMessage id="Listings.clearAll" />
          </ResetBtn>
        </CounterWrapper>
      </ActiveBar>
      <ScrollContainer ref={scrollContainerRef}>
        {MyBidsFilter.Applied && (
          <MyBidsFilter.Applied
            filtersState={state.filters}
            updateFilters={updateFilters}
          />
        )}
        {NewSellerFilter.Applied && (
          <NewSellerFilter.Applied
            filtersState={state.filters}
            updateFilters={updateFilters}
          />
        )}
        {categoryFilterMissingFromSidebar && CategoryFilter.Applied && (
          <CategoryFilter.Applied
            filtersState={state.filters}
            updateFilters={updateFilters}
          />
        )}
        <StoreFrontIdFilter.Applied
          filtersState={state.filters}
          updateFilters={updateFilters}
        />
        <EndTimeRangeFilter.Applied
          filtersState={state.filters}
          updateFilters={updateFilters}
        />
        <FiltersList
          filterComponents={filters}
          filtersState={state.filters}
          updateFilters={updateFilters}
        />
        <CategoryThresholdApplied
          filtersState={state.filters}
          updateFilters={updateFilters}
        />
      </ScrollContainer>
    </Wrapper>
  )
}
